/**
 * B614 — the SHOW face's poster wall (553:2 @1440, 555:130 @390): the head
 * ("THIS MONTH AT <club>", the month, one line of house facts) over a wall of
 * package PosterCards, soonest night first. The derivations live in
 * lib/posterWall.ts; this file composes them and holds no rule of its own.
 * The head carries the POSTERS · CALENDAR switch (553:2 / 554:2); the
 * calendar view (leg (a′), lib/showCalendar.ts) swaps the wall for the month
 * of every show's nights, its arrows bound to this month … the last show on
 * sale, and a foot that teaches the door and repeats the house facts.
 */
import type { ReactElement } from "react";
import { Button, PosterCard, SegmentedControl, ShowCalendar, type PosterNight } from "@transact/ui";
import { lineupLine } from "@transact/contracts";
import { posterAge, posterFlag, posterPrices, wallHead, wallOrder } from "../lib/posterWall.js";
import { calendarDays, calendarSpan, monthName, monthTitle, shiftMonth, type CalendarFeed } from "../lib/showCalendar.js";
import type { PoolAvailability, ProductSummary, SlotSummary } from "./screens.js";

export type ShowView = "posters" | "calendar";

export interface PosterFeed {
  /** The product's ON_SALE slots (any window — the derivations cut the 14 days). */
  readonly slots: readonly SlotSummary[];
  readonly nights: readonly PosterNight[];
  /** slotId → its pools, for the slots read (the poster's 14 days); absent = unread. */
  readonly availability: Readonly<Record<string, readonly PoolAvailability[]>>;
}

export function PosterWall(props: {
  readonly products: readonly ProductSummary[];
  /** productId → its read slots and derived nights; absent = not read (yet, or refused). */
  readonly feeds: ReadonlyMap<string, PosterFeed>;
  readonly clubName?: string;
  readonly now: number;
  readonly loading?: boolean;
  readonly onOpen: (productId: string) => void;
  readonly onShowtime: (productId: string, slotId: string, start: number) => void;
  /** B614 (a′) — a calendar tile: that show's tickets step with the night chosen. */
  readonly onNight: (productId: string, slotId: string, start: number) => void;
  readonly view: ShowView;
  readonly onView: (view: ShowView) => void;
  /** "2026-10" — the calendar's month. */
  readonly month: string;
  readonly onMonth: (month: string) => void;
}): ReactElement {
  const head = wallHead(
    props.clubName,
    props.products,
    props.products.map((p) => props.feeds.get(p.productId)?.nights ?? []),
    props.now,
  );
  const first = new Map<string, number>();
  for (const p of props.products) {
    const id = props.feeds.get(p.productId)?.nights[0]?.showtimes[0]?.id;
    const at = props.feeds.get(p.productId)?.slots.find((s) => s.slotId === id)?.start;
    if (at !== undefined) first.set(p.productId, at);
  }
  const ordered = wallOrder(props.products, first);
  const headliner = (p: ProductSummary): string => p.presentation?.lineup?.headliner ?? p.name;
  const calFeeds: CalendarFeed[] = ordered.flatMap((p) => {
    const f = props.feeds.get(p.productId);
    return f === undefined ? [] : [{ productId: p.productId, name: headliner(p), slots: f.slots, availability: f.availability }];
  });
  const span = calendarSpan(calFeeds, props.now);
  const calendar = props.view === "calendar";
  const prev = shiftMonth(props.month, -1);
  const next = shiftMonth(props.month, 1);
  const month = calendar ? calendarDays(calFeeds, props.month, props.now) : undefined;
  return (
    <section className="poster-wall" aria-labelledby="poster-wall-title" data-redline="show-poster-wall">
      <header className="poster-wall-head">
        <div className="poster-wall-words">
          <p className="poster-wall-eyebrow">{calendar ? "Calendar" : head.eyebrow}</p>
          <h1 className="poster-wall-title" id="poster-wall-title">
            {calendar ? monthTitle(props.month) : head.title}
          </h1>
          {!calendar && head.facts !== undefined ? <p className="poster-wall-facts">{head.facts}</p> : null}
        </div>
        <div className="poster-wall-controls">
          <SegmentedControl
            legend="Show the listing as"
            name="show-view"
            options={[
              { value: "posters", label: "Posters" },
              { value: "calendar", label: "Calendar" },
            ]}
            value={props.view}
            onChange={(v) => props.onView(v === "calendar" ? "calendar" : "posters")}
            redline="show-view-switch"
          />
          {calendar ? (
            <nav className="poster-wall-months" aria-label="Months">
              {/* Past the horizon the month is drawn dim, not as a shut door (554:2's
                  '‹ SEPTEMBER' in the dim ink): there is nothing there to open. */}
              {prev < span.first ? (
                <span className="poster-wall-month-edge">‹ {monthName(prev)}</span>
              ) : (
                <Button variant="textlink" ariaLabel={`Previous month, ${monthName(prev)}`} onClick={() => props.onMonth(prev)}>
                  ‹ {monthName(prev)}
                </Button>
              )}
              {next > span.last ? (
                <span className="poster-wall-month-edge">{monthName(next)} ›</span>
              ) : (
                <Button variant="textlink" ariaLabel={`Next month, ${monthName(next)}`} onClick={() => props.onMonth(next)}>
                  {monthName(next)} ›
                </Button>
              )}
            </nav>
          ) : null}
        </div>
      </header>
      {month !== undefined ? (
        <>
          <ShowCalendar
            label={monthTitle(props.month)}
            leading={month.leading}
            days={month.days.map((d) => ({ ...d, shows: d.shows.map((x) => ({ id: `${x.productId}|${x.slotId}`, name: x.name, times: x.times, label: x.label })) }))}
            todayLabel="Today"
            onShow={(id) => {
              const hit = month.days.flatMap((d) => d.shows).find((x) => `${x.productId}|${x.slotId}` === id);
              if (hit !== undefined) props.onNight(hit.productId, hit.slotId, hit.start);
            }}
            redline="show-calendar-grid"
          />
          <p className="poster-wall-foot">{["Tap a show for tickets", ...(head.facts !== undefined ? [head.facts] : [])].join(" · ")}</p>
        </>
      ) : (
      <ul className="poster-grid">
        {ordered.map((p, i) => {
          const feed = props.feeds.get(p.productId);
          const nights = feed?.nights ?? [];
          const age = posterAge(p.presentation?.policies?.minAge, feed?.slots ?? [], props.now);
          const flag = posterFlag(nights);
          const prices = posterPrices(p);
          const lineup = p.presentation?.lineup;
          const rest = lineup !== undefined ? lineupLine(lineup) : undefined;
          const photo = p.presentation?.photos?.[0];
          const startOf = (slotId: string): number => feed?.slots.find((s) => s.slotId === slotId)?.start ?? props.now;
          return (
            <li key={p.productId}>
              <PosterCard
                headliner={lineup?.headliner ?? p.name}
                {...(rest !== undefined ? { lineup: rest } : {})}
                {...(photo !== undefined ? { image: { url: photo.url, ...(photo.alt !== undefined ? { alt: photo.alt } : {}) } } : {})}
                {...(age !== undefined ? { age } : {})}
                {...(flag !== undefined ? { flag } : {})}
                nights={nights}
                {...(feed !== undefined || props.loading !== true ? { noNights: "No shows in the next two weeks — see every date on the show's page." } : {})}
                {...(prices.price !== undefined ? { price: prices.price } : {})}
                {...(prices.detail !== undefined ? { priceDetail: prices.detail } : {})}
                onOpen={() => props.onOpen(p.productId)}
                openLabel={`${lineup?.headliner ?? p.name} — see the nights and get tickets`}
                onShowtime={(slotId) => props.onShowtime(p.productId, slotId, startOf(slotId))}
                {...(i === 0 ? { redline: "show-poster-1" } : {})}
              />
            </li>
          );
        })}
      </ul>
      )}
    </section>
  );
}
